import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  Refresh,
  TableChart,
  Launch,
  Share,
  Sync,
  CheckCircle,
  Info
} from '@mui/icons-material';
import SubmissionCard from '../components/forms/SubmissionCard';
import SubmissionCompactRow from '../components/forms/SubmissionCompactRow';

const VIEW_MODE_PREFIX = 'formViewMode';

const SiteForms = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [formsData, setFormsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorSeverity, setErrorSeverity] = useState('error'); // 'error', 'warning', 'info', 'success'
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [creatingSheet, setCreatingSheet] = useState(false);
  const [syncingSheet, setSyncingSheet] = useState(false);
  const [sheetInfo, setSheetInfo] = useState(null);
  const [emailDialog, setEmailDialogInternal] = useState({ open: false, emails: '', type: 'sync' });
  const [syncResultDialog, setSyncResultDialog] = useState({ open: false, result: null });

  // Ref to prevent dialog from closing immediately after opening
  const dialogOpeningRef = useRef(false);

  // State to track if dialog is ready for interaction
  const [dialogReady, setDialogReady] = useState(false);

  // Track if we've had at least one render after ready
  const hasRenderedReadyRef = useRef(false);

  // Ref for the submit button
  const submitButtonRef = useRef(null);

  // Ref to store the latest emailDialog state (to avoid closure issues)
  const emailDialogRef = useRef(emailDialog);

  // Debug wrapper to track all setEmailDialog calls
  const setEmailDialog = (newValue) => {
    emailDialogRef.current = newValue; // Update ref to always have latest value
    setEmailDialogInternal(newValue);
  };
  const [dateRange, setDateRange] = useState({
    fromDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0]
  });

  const [viewModes, setViewModes] = useState({});

  const getViewMode = (formName) => viewModes[formName] || 'card';

  const setViewMode = useCallback((formName, mode) => {
    if (!mode) return; // ToggleButtonGroup passes null when same button clicked
    setViewModes(prev => ({ ...prev, [formName]: mode }));
    try {
      localStorage.setItem(`${VIEW_MODE_PREFIX}:${id}:${formName}`, mode);
    } catch (e) {
      // localStorage may be unavailable; silently ignore
    }
  }, [id]);

  // Effect to set dialog ready state after it opens
  useEffect(() => {
    if (emailDialog.open) {
      setDialogReady(false);
      hasRenderedReadyRef.current = false;

      // Capture ref value for cleanup
      const buttonRef = submitButtonRef.current;

      const readyTimer = setTimeout(() => {
        setDialogReady(true);
        hasRenderedReadyRef.current = true;

        // Attach click handler programmatically
        if (submitButtonRef.current) {
          const handleClick = (event) => {
            handleEmailDialogSubmit(event);
          };

          // Material-UI Button ref points directly to button element
          const buttonElement = submitButtonRef.current;
          buttonElement.addEventListener('click', handleClick);
          buttonElement._clickHandler = handleClick;
        }
      }, 500);

      return () => {
        clearTimeout(readyTimer);
        // Remove click handler on cleanup
        if (buttonRef && buttonRef._clickHandler) {
          buttonRef.removeEventListener('click', buttonRef._clickHandler);
          delete buttonRef._clickHandler;
        }
      };
    } else {
      setDialogReady(false);
      hasRenderedReadyRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailDialog.open]);

  useEffect(() => {
    fetchSiteInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (site) {
      fetchForms();
      fetchGoogleSheetInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site]);

  useEffect(() => {
    if (!formsData?.data) return;
    const next = {};
    ['gravity_forms', 'contact_form_7'].forEach(formType => {
      const forms = formsData.data[formType] || {};
      Object.keys(forms).forEach(formName => {
        try {
          const stored = localStorage.getItem(`${VIEW_MODE_PREFIX}:${id}:${formName}`);
          if (stored === 'card' || stored === 'compact') {
            next[formName] = stored;
          }
        } catch (e) { /* ignore */ }
      });
    });
    setViewModes(next);
  }, [formsData, id]);

  const fetchSiteInfo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/sites/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSite(data);
      } else {
        setError('Failed to fetch site information');
      }
    } catch (error) {
      setError('Failed to fetch site information');
      console.error('Error fetching site info:', error);
    }
  };

  const fetchForms = useCallback(async (customDateRange = null) => {
    if (!site) return;

    setLoading(true);
    setError('');

    // Use custom date range if provided, otherwise use current state
    const currentDateRange = customDateRange || dateRange;

    try {
      // Use backend API endpoint instead of direct WordPress access
      const params = new URLSearchParams({
        fromDate: `${currentDateRange.fromDate} 00:00:00`,
        toDate: `${currentDateRange.toDate} 23:59:59`
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/forms/${id}?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFormsData(data);
        setLastFetchTime(new Date());
      } else {
        const errorText = await response.text();
        setError(`Failed to fetch form entries: ${response.status} ${errorText}`);
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [site, dateRange, id]); // Add dependencies

  const fetchGoogleSheetInfo = async () => {
    if (!site) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/google-sheets/${id}/google-sheet-info`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSheetInfo(data);
      }
    } catch (error) {
      console.error('Error fetching Google Sheet info:', error);
    }
  };

  const handleSyncWithEmails = async (shareEmails = null) => {
    setSyncingSheet(true);
    setError('');

    // Use provided emails or existing emails from site data
    const emailsToUse = shareEmails || (() => {
      try {
        return JSON.parse(site?.google_sheet_shared_emails || '[]');
      } catch {
        return [];
      }
    })();

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/google-sheets/${id}/create-google-sheet`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fromDate: `${dateRange.fromDate} 00:00:00`,
          toDate: `${dateRange.toDate} 23:59:59`,
          share_emails: emailsToUse
        })
      });
      
      if (response.ok) {
        const data = await response.json();

        // Show sync result dialog
        setSyncResultDialog({
          open: true,
          result: {
            success: !data.partial_sync,
            partial: data.partial_sync || false,
            progress: data.progress,
            spreadsheetUrl: data.spreadsheet_url
          }
        });

        // Refresh site data to get updated Google Sheets info
        await fetchSiteInfo();
        await fetchGoogleSheetInfo();
      } else {
        const errorData = await response.json();
        setErrorSeverity('error');
        setError(`Failed to sync Google Sheet: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      setError(`Network error while syncing Google Sheet: ${err.message}`);
    } finally {
      setSyncingSheet(false);
    }
  };

  const handleCreateGoogleSheet = async (shareEmails = null) => {
    if (!site) return;
    
    // Ensure shareEmails is an array or null (not an event object)
    const cleanShareEmails = Array.isArray(shareEmails) ? shareEmails : null;
    
    // Check if we need to ask for email addresses
    const hasExistingEmails = sheetInfo?.shared_emails && sheetInfo.shared_emails.length > 0;
    const shouldAskForEmails = !hasExistingEmails && cleanShareEmails === null;
    
    if (shouldAskForEmails) {
      // Show email dialog instead of creating immediately
      setEmailDialog({ 
        open: true, 
        emails: sheetInfo?.shared_emails ? sheetInfo.shared_emails.join(', ') : '' 
      });
      return;
    }
    
    setCreatingSheet(true);
    setError('');
    
    // Use provided emails, existing emails, or empty array
    const emailsToUse = cleanShareEmails || sheetInfo?.shared_emails || [];
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/google-sheets/${id}/create-google-sheet`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fromDate: `${dateRange.fromDate} 00:00:00`,
          toDate: `${dateRange.toDate} 23:59:59`,
          share_emails: emailsToUse
        })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        // Update local sheet info immediately
        setSheetInfo({
          ...sheetInfo,
          google_sheet_url: result.spreadsheet_url,
          google_sheet_id: result.spreadsheet_id,
          has_google_sheet: true,
          google_sheet_last_updated: result.last_updated,
          shared_emails: result.shared_emails || emailsToUse
        });
        
        // Refresh site data and Google Sheet info from backend
        await fetchSiteInfo();
        await fetchGoogleSheetInfo();
        
        // Open the Google Sheet in a new tab
        window.open(result.spreadsheet_url, '_blank');
        
        setError(''); // Clear any previous errors
        
      } else {
        const errorMessage = result.message || 'Failed to create Google Sheet';
        const errorDetails = result.error_details ? `\n\nDetails: ${result.error_details}` : '';
        setError(`${errorMessage}${errorDetails}`);
      }
    } catch (err) {
      setError(`Error creating Google Sheet: ${err.message}`);
    } finally {
      setCreatingSheet(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailDialogSubmit = (event) => {

    // Use ref to get latest state (avoid closure issues)
    const latestEmailDialog = emailDialogRef.current;

    // Prevent any default form submission behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Guard: Don't process if dialog hasn't rendered in ready state yet
    if (!hasRenderedReadyRef.current) {
      return;
    }

    // Guard: Don't process if already syncing or creating
    if (syncingSheet || creatingSheet) {
      return;
    }

    // Guard: Don't process if dialog is not open
    if (!latestEmailDialog.open) {
      return;
    }

    // Parse comma-separated emails and trim whitespace
    const emailList = latestEmailDialog.emails
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);

    // Validate emails
    const invalidEmails = emailList.filter(email => !validateEmail(email));
    if (invalidEmails.length > 0) {
      setError(`Invalid email addresses: ${invalidEmails.join(', ')}. Please enter valid email addresses.`);
      return;
    }

    // Close dialog
    setEmailDialog({ open: false, emails: '', type: 'sync' });

    if (latestEmailDialog.type === 'sync') {
      handleSyncWithEmails(emailList);
    } else {
      // Handle create case
      handleCreateGoogleSheet(emailList);
    }
  };

  const handleEmailDialogCancel = () => {
    setEmailDialog({ open: false, emails: '', type: 'sync' });
  };

  const getDatePresets = () => {
    const today = new Date().toISOString().split('T')[0];
    const startOfWeek = new Date();
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);

    return [
      { label: 'Today', from: today, to: today },
      { label: 'This Week', from: startOfWeek.toISOString().split('T')[0], to: today },
      { label: 'This Month', from: firstOfMonth.toISOString().split('T')[0], to: today },
      { label: 'Last 30 Days', from: thirtyDaysAgo.toISOString().split('T')[0], to: today },
    ];
  };

  const handleDateChange = (field, value) => {
    const newDateRange = { ...dateRange, [field]: value };
    setDateRange(newDateRange);
    if (newDateRange.fromDate && newDateRange.toDate && newDateRange.fromDate <= newDateRange.toDate) {
      fetchForms(newDateRange);
    }
  };

  const handleRefresh = () => {
    if (site) {
      fetchForms({
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate
      });
    }
  };

  const renderFormSection = (formType, sectionTitle, sectionColor) => {
    const formsForType = formsData?.data?.[formType];
    if (!formsForType) return null;

    return (
      <Paper elevation={3} sx={{ mb: 3 }}>
        <Box sx={{ p: 2, bgcolor: sectionColor, color: 'white' }}>
          <Typography variant="h6">{sectionTitle}</Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          {Object.keys(formsForType).length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No {sectionTitle} found for this period
            </Typography>
          ) : (
            Object.entries(formsForType).map(([formName, submissions]) => {
              const mode = getViewMode(formName);
              return (
                <Accordion
                  key={formName}
                  disableGutters
                  elevation={0}
                  square
                  sx={{
                    border: 'none',
                    boxShadow: 'none',
                    backgroundColor: 'transparent',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                      <Typography variant="h6">
                        {formName} ({submissions.length} submissions)
                      </Typography>
                      <ToggleButtonGroup
                        aria-label="View mode"
                        size="small"
                        value={mode}
                        exclusive
                        onChange={(e, newMode) => {
                          e.stopPropagation();
                          setViewMode(formName, newMode);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '11px', py: 0.25, px: 1 } }}
                      >
                        <ToggleButton value="card">Card</ToggleButton>
                        <ToggleButton value="compact">Compact</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {submissions.length === 0 ? (
                      <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                        No submissions found for this period
                      </Typography>
                    ) : (
                      <Box>
                        {submissions.map((submission, idx) => {
                          const Component = mode === 'compact' ? SubmissionCompactRow : SubmissionCard;
                          return (
                            <Component
                              key={submission.ID ?? submission.id ?? idx}
                              submission={submission}
                              formType={formType}
                            />
                          );
                        })}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Box>
      </Paper>
    );
  };

  if (loading && !formsData) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Box sx={{ py: { xs: 2, md: 4 } }}>
        <Container maxWidth="xl">
          <Box sx={{ mb: { xs: 2, md: 4 } }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/sites')}
              sx={{ mb: 2, fontSize: { xs: '0.875rem', md: '1rem' } }}
            >
              Back to Sites
            </Button>

          <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
            Form Submissions
            {site && ` - ${site.rootdomain}`}
          </Typography>

        <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Date Range Filter
          </Typography>
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="From Date"
                value={dateRange.fromDate}
                onChange={(e) => handleDateChange('fromDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="To Date"
                value={dateRange.toDate}
                onChange={(e) => handleDateChange('toDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', height: '100%' }}>
                {getDatePresets().map(preset => (
                  <Button
                    key={preset.label}
                    size="small"
                    variant={dateRange.fromDate === preset.from && dateRange.toDate === preset.to ? 'contained' : 'outlined'}
                    onClick={() => {
                      const newRange = { fromDate: preset.from, toDate: preset.to };
                      setDateRange(newRange);
                      fetchForms(newRange);
                    }}
                    disabled={loading || !site}
                  >
                    {preset.label}
                  </Button>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <Refresh />}
                onClick={() => {
                  handleRefresh();
                }}
                disabled={loading || !site}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                {loading ? 'Loading...' : 'Fetch Data'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Information Alert */}
        <Alert
          severity="info"
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Important Notes:
          </Typography>
          <Typography variant="body2" component="div">
            • If you want to see updated form entries in Google Sheets, please click the "Sync Now" button before opening the Google Sheets link.
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 1 }}>
            • <strong>IMPORTANT:</strong> We have a scheduled sync of form entries to Google Sheets every 2 hours. Please export or save your selected data before it can be overwritten by the automated sync.
          </Typography>
        </Alert>

        {/* Google Sheets Integration Status */}
        {site && (
          <Paper elevation={2} sx={{ p: { xs: 2, md: 4 }, mb: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <TableChart sx={{ fontSize: { xs: 24, md: 28 }, mr: 2, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                Google Sheets Integration
              </Typography>
            </Box>

            {site.google_sheet_id ? (
              <Box>
                <Grid container spacing={3} alignItems="stretch">
                  <Grid item xs={12} md={8}>
                    <Box sx={{
                      p: { xs: 2, md: 3 },
                      bgcolor: 'success.light',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'success.main'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CheckCircle sx={{ mr: 2, color: 'success.dark' }} />
                        <Typography variant="body1" fontWeight={600} color="success.dark" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                          Connected to Google Sheets
                        </Typography>
                      </Box>

                      <Typography variant="body2" color="success.dark" sx={{ mb: 2, fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                        Form submissions are automatically synced to your Google Sheet
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Chip
                          icon={<Share />}
                          label={`Shared with: ${(() => {
                            try {
                              const emails = JSON.parse(site.google_sheet_shared_emails || '[]');
                              return Array.isArray(emails) ? emails.join(', ') : 'No shared emails';
                            } catch {
                              return site.google_sheet_shared_emails || 'No shared emails';
                            }
                          })()}`}
                          variant="outlined"
                          color="success"
                          sx={{ borderRadius: 2, fontSize: { xs: '0.75rem', md: '0.8125rem' } }}
                        />
                        {site.google_sheet_last_updated && (
                          <Chip
                            icon={<Sync />}
                            label={`Last updated: ${new Date(site.google_sheet_last_updated).toLocaleString()}`}
                            variant="filled"
                            color="success"
                            sx={{ borderRadius: 2, fontSize: { xs: '0.75rem', md: '0.8125rem' } }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<Launch />}
                        onClick={() => window.open(site.google_sheet_url, '_blank')}
                        sx={{
                          borderRadius: 2,
                          py: 1.5,
                          fontWeight: 600,
                          textTransform: 'none',
                          fontSize: { xs: '0.875rem', md: '1rem' }
                        }}
                      >
                        Open in Google Sheets
                      </Button>

                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={syncingSheet ? <CircularProgress size={20} /> : <Sync />}
                        onClick={() => {

                          // Set the ref flag to prevent immediate closure
                          dialogOpeningRef.current = true;

                          // Show email dialog with current shared emails pre-filled
                          const currentEmails = (() => {
                            try {
                              const emails = JSON.parse(site?.google_sheet_shared_emails || '[]');
                              return Array.isArray(emails) ? emails.join(', ') : '';
                            } catch {
                              return '';
                            }
                          })();

                          setEmailDialog({
                            open: true,
                            emails: currentEmails,
                            type: 'sync'
                          });

                          // Clear the ref flag after a short delay to allow the dialog to open
                          setTimeout(() => {
                            dialogOpeningRef.current = false;
                          }, 200);
                        }}
                        disabled={syncingSheet}
                        sx={{
                          borderRadius: 2,
                          py: 1.5,
                          fontWeight: 600,
                          textTransform: 'none',
                          borderColor: 'success.main',
                          color: 'success.main',
                          fontSize: { xs: '0.875rem', md: '1rem' },
                          '&:hover': {
                            borderColor: 'success.dark',
                            backgroundColor: 'success.light',
                            color: 'success.dark'
                          }
                        }}
                      >
                        {syncingSheet ? 'Syncing...' : 'Sync Now'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Box sx={{
                p: { xs: 2, md: 3 },
                bgcolor: 'info.light',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'info.main',
                textAlign: 'center'
              }}>
                <TableChart sx={{ fontSize: { xs: 40, md: 48 }, color: 'info.dark', mb: 2, opacity: 0.7 }} />
                <Typography variant="h6" color="info.dark" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                  Google Sheets Not Connected
                </Typography>
                <Typography variant="body2" color="info.dark" sx={{ mb: 3, maxWidth: 500, mx: 'auto', fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                  Connect this site to Google Sheets to automatically export form submissions to a shareable spreadsheet
                </Typography>
                <Button
                  variant="contained"
                  startIcon={creatingSheet ? <CircularProgress size={20} /> : <TableChart />}
                  onClick={() => {
                    // Set the ref flag to prevent immediate closure
                    dialogOpeningRef.current = true;

                    setEmailDialog({
                      open: true,
                      emails: '',
                      type: 'create'
                    });

                    // Clear the ref flag after a short delay to allow the dialog to open
                    setTimeout(() => {
                      dialogOpeningRef.current = false;
                    }, 200);
                  }}
                  disabled={creatingSheet || !formsData}
                  sx={{
                    borderRadius: 2,
                    px: { xs: 3, md: 4 },
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: { xs: '0.875rem', md: '1rem' }
                  }}
                >
                  {creatingSheet ? 'Creating...' : 'Create Google Sheet'}
                </Button>
                {!formsData && (
                  <Typography variant="caption" display="block" color="info.dark" sx={{ mt: 1, opacity: 0.8, fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                    Fetch form data first to create the Google Sheet
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        )}

        {error && (
          <Alert severity={errorSeverity} sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {error}
          </Alert>
        )}

        {formsData && (
          <Box>
            <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'primary.light', color: 'white' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Showing Results: {formsData.fromDate} to {formsData.toDate}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Total entries found: {formsData.data ? Object.values(formsData.data).reduce((total, formType) => {
                  return total + Object.values(formType).reduce((sum, entries) => sum + entries.length, 0);
                }, 0) : 0}
                {lastFetchTime && ` • Last updated: ${lastFetchTime.toLocaleTimeString()}`}
                {sheetInfo?.has_google_sheet && sheetInfo?.google_sheet_last_updated && (
                  ` • Google Sheet: ${new Date(sheetInfo.google_sheet_last_updated).toLocaleString()}`
                )}
              </Typography>
            </Paper>

            {renderFormSection('gravity_forms', 'Gravity Forms Submissions', 'primary.main')}
            {renderFormSection('contact_form_7', 'Contact Form 7 Submissions', 'secondary.main')}

            {(!formsData.data.gravity_forms || Object.keys(formsData.data.gravity_forms).length === 0) &&
             (!formsData.data.contact_form_7 || Object.keys(formsData.data.contact_form_7).length === 0) && (
              <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">
                  No form submissions found
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Try adjusting the date range or check if the WordPress plugin is properly configured.
                </Typography>
              </Paper>
            )}
          </Box>
        )}
        </Box>
      </Container>
    </Box>

    {/* Email Sharing Dialog */}
    {emailDialog.open && (
      <Dialog
        open={true}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return; // Prevent closing on backdrop click or escape
          }
          handleEmailDialogCancel();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        disableRestoreFocus
        disableAutoFocus
      >
      <DialogTitle>
        {emailDialog.type === 'sync' ? 'Update Google Sheet Sharing' : 'Share Google Sheet'}
      </DialogTitle>
      <DialogContent>
        <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {emailDialog.type === 'sync'
              ? 'Update the email addresses to share the Google Sheet with (comma-separated):'
              : 'Enter email addresses to share the Google Sheet with (comma-separated):'}
          </Typography>
          <TextField
          fullWidth
          multiline
          rows={3}
          label="Email Addresses"
          placeholder="user1@example.com, user2@example.com, user3@example.com"
          value={emailDialog.emails}
          onChange={(e) => {
            setEmailDialog({ ...emailDialog, emails: e.target.value });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          helperText="Separate multiple email addresses with commas. Leave empty to create without sharing."
          sx={{ mt: 1 }}
          autoFocus
        />
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleEmailDialogCancel} type="button">
          Cancel
        </Button>
        <Button
          ref={submitButtonRef}
          type="button"
          variant="contained"
          disabled={syncingSheet || creatingSheet || !dialogReady}
        >
          {emailDialog.type === 'sync'
            ? (syncingSheet ? 'Syncing...' : 'Sync & Update Sharing')
            : (creatingSheet ? 'Creating...' : 'Create & Share')}
        </Button>
      </DialogActions>
    </Dialog>
    )}

    {/* Sync Result Dialog */}
    <Dialog
      open={syncResultDialog.open}
      onClose={() => setSyncResultDialog({ open: false, result: null })}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          overflowX: 'hidden',
          overflowY: 'auto'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflowX: 'hidden' }}>
          {syncResultDialog.result?.success ? (
            <>
              <CheckCircle color="success" />
              <Typography variant="h6" sx={{ wordWrap: 'break-word' }}>Sync Completed Successfully!</Typography>
            </>
          ) : syncResultDialog.result?.partial ? (
            <>
              <Info color="warning" />
              <Typography variant="h6" sx={{ wordWrap: 'break-word' }}>Partial Sync Completed</Typography>
            </>
          ) : null}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ overflowX: 'hidden', overflowY: 'auto' }}>
        {syncResultDialog.result?.progress && (
          <Box sx={{ overflowX: 'hidden' }}>
            {/* Summary */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: syncResultDialog.result.success ? 'success.light' : 'warning.light', overflowX: 'hidden' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: syncResultDialog.result.success ? 'success.dark' : 'warning.dark', wordWrap: 'break-word' }}>
                {syncResultDialog.result.progress.successful.length} of {syncResultDialog.result.progress.total_sheets} sheets synced successfully
              </Typography>
            </Paper>

            {/* Successful Sheets */}
            {syncResultDialog.result.progress.successful.length > 0 && (
              <Box sx={{ mb: 2, overflowX: 'hidden' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'success.main', wordWrap: 'break-word' }}>
                  ✓ Successfully Synced ({syncResultDialog.result.progress.successful.length})
                </Typography>
                <Paper elevation={0} sx={{ maxHeight: 200, overflowY: 'auto', overflowX: 'hidden', bgcolor: 'grey.50', p: 2, width: '100%' }}>
                  {syncResultDialog.result.progress.successful.map((sheet, idx) => (
                    <Typography key={idx} variant="body2" sx={{ py: 0.5, color: '#212121', fontWeight: 500, wordWrap: 'break-word', overflowWrap: 'break-word', width: '100%' }}>
                      • {sheet.name} ({sheet.rows} rows)
                    </Typography>
                  ))}
                </Paper>
              </Box>
            )}

            {/* Failed Sheets */}
            {syncResultDialog.result.progress.failed.length > 0 && (
              <Box sx={{ mb: 2, overflowX: 'hidden' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'error.main', wordWrap: 'break-word' }}>
                  ✗ Failed ({syncResultDialog.result.progress.failed.length})
                </Typography>
                <Paper elevation={0} sx={{ maxHeight: 200, overflowY: 'auto', overflowX: 'hidden', bgcolor: 'grey.50', p: 2, width: '100%' }}>
                  {syncResultDialog.result.progress.failed.map((sheet, idx) => (
                    <Box key={idx} sx={{ py: 1, borderBottom: idx < syncResultDialog.result.progress.failed.length - 1 ? '1px solid' : 'none', borderColor: 'grey.200', width: '100%', overflowX: 'hidden' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#212121', mb: 0.5, wordWrap: 'break-word', overflowWrap: 'break-word', width: '100%' }}>
                        • {sheet.name}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', pl: 2, color: '#c62828', fontWeight: 400, wordWrap: 'break-word', overflowWrap: 'break-word', width: '100%' }}>
                        {sheet.error}
                      </Typography>
                    </Box>
                  ))}
                </Paper>

                {syncResultDialog.result.progress.failed.some(f => f.is_quota_error) && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Some sheets failed due to Google API quota limits. Wait 60 seconds and click "Sync Now" again to retry the failed sheets.
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {syncResultDialog.result?.spreadsheetUrl && (
          <Button
            onClick={() => window.open(syncResultDialog.result.spreadsheetUrl, '_blank')}
            startIcon={<Launch />}
            variant="outlined"
          >
            Open in Google Sheets
          </Button>
        )}
        <Button onClick={() => setSyncResultDialog({ open: false, result: null })} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {/* Loading Overlay - Blocks all interactions during sync */}
    <Backdrop
      open={syncingSheet || creatingSheet}
      sx={{
        color: '#fff',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        flexDirection: 'column',
        gap: 2
      }}
    >
      <Box
        sx={{
          fontSize: { xs: '60px', md: '80px' },
          animation: 'dance 0.6s ease-in-out infinite alternate',
          '@keyframes dance': {
            '0%': {
              transform: 'translateY(0px) rotate(-5deg)',
            },
            '50%': {
              transform: 'translateY(-20px) rotate(5deg) scale(1.1)',
            },
            '100%': {
              transform: 'translateY(0px) rotate(-5deg)',
            },
          },
        }}
      >
        🐱
      </Box>
      <CircularProgress color="inherit" size={{ xs: 50, md: 60 }} />
      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', md: '1.25rem' }, textAlign: 'center', px: 2 }}>
        {syncingSheet ? 'Syncing to Google Sheets...' : 'Creating Google Sheet...'}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.8, fontSize: { xs: '0.875rem', md: '1rem' }, textAlign: 'center', px: 2 }}>
        Please wait, this may take a moment
      </Typography>
    </Backdrop>
    </>
  );
};

export default SiteForms;